import Api from '../../Api';
import { ICoreUser } from '../../shared-interfaces/User/ICoreUser';
import { IPagedResult } from '../../shared-interfaces/User/IPagedResult';
import { IRetentionStatus } from '../../shared-interfaces/User/IRetentionStatus';
import { IUserRetentionStatus } from '../../shared-interfaces/User/IUserRetentionStatus';

const retentionStatusesCache = [] as IRetentionStatus[][];

export enum OrderUserSearch {
  OpenAlerts = 0,
  TotalAlerts = 1,
  CurrentTermOpenAlerts = 2,
  CurrentTermTotalAlerts = 3,
  RiskScoreId = 4,
  FirstName = 5,
}

export enum SearchTypes {
  like = 0,
  matchOnExternalId = 1,
}

export interface ISearchCoreUserParams {
  term?: string;
  roles?: number[];
  retentionStatuses?: number[];
  take?: number;
  skip?: number;
  inNetwork?: boolean;
  orderBy?: OrderUserSearch;
}

export interface ISearchCoreUserParamsV2 {
  userIds?: string[];
  term?: string;
  RoleIds?: number[];
  RetentionStatusIds?: number[];
  take?: number;
  skip?: number;
  inNetwork?: boolean;
  orderBy?: OrderUserSearch;
  enrolledOnly?: boolean;
  SearchType?: SearchTypes;
}

const UserService = {
  multiSearchCoreUser: async (
    externalIds: string[],
    roles?: number[],
  ): Promise<ICoreUser[]> => {
    const response = await Api.post(`user/GetCoreUsersWithSearchTerms`, {
      externalIds: externalIds.map(x => x.trim()),
      roles: roles,
    });
    return response.data as ICoreUser[];
  },
  //Not Deprecated but should only be used for the widget
  searchCoreUser: async ({
    term,
    roles = [] as number[],
    retentionStatuses = [] as number[],
    take = undefined,
    skip = undefined,
  }: ISearchCoreUserParams) => {
    const response = await Api.get('user/SearchCoreUsers', {
      term: term,
      take: take,
      skip: skip,
      roles: roles,
      retentionStatuses: retentionStatuses,
    });
    return response.data as ICoreUser[];
  },
  searchAssignableUser: async (
    studentId: string,
    search: string,
    roles?: number[],
    take?: number,
    skip?: number,
  ) => {
    const response = await Api.get('user/SearchForAssignableUsers', {
      studentId: studentId,
      search: search,
      roles: roles,
      take: take,
      skip: skip,
    });
    return response.data as ICoreUser[];
  },
  getCoreUsers: async (userIds: string[]) => {
    const response = await Api.get('user/GetCoreUsers', { userIds: userIds });
    return response.data as ICoreUser[];
  },
  getStudentNetwork: async (studentId: string) => {
    const response = await Api.get(
      `user/GetStudentNetwork?studentId=${studentId}`,
    );
    return response.data as ICoreUser[];
  },
  importUsers: async (externalIds: string[]) => {
    const response = await Api.post('user/import', externalIds);
    return response.data;
  },
  getRetentionStatuses: async (): Promise<{
    [id: number]: IRetentionStatus;
  }> => {
    if (retentionStatusesCache.length > 0) return retentionStatusesCache[0];
    const response = await Api.get('retention-status');
    retentionStatusesCache.push(
      Object.assign(
        {},
        ...response.data.map((x: IRetentionStatus) => ({ [x.id]: x })),
      ),
    );
    return retentionStatusesCache[0];
  },
  getUserCurrentRetentionStatus: async (
    userId: string,
  ): Promise<IUserRetentionStatus> => {
    const response = await Api.get(`user/${userId}/retention-status`);
    return response.data;
  },
  getUsersCurrentRetentionStatus: async (
    userIds: string[],
  ): Promise<IUserRetentionStatus[]> => {
    const response = await Api.post('user/retention-status', userIds);
    return response.data;
  },
  search: async (
    searchParameters: ISearchCoreUserParamsV2,
  ): Promise<IPagedResult<ICoreUser>> => {
    const response = await Api.post(
      `user/search?top=${searchParameters.take}&skip=${searchParameters.skip}`,
      searchParameters,
    );
    return response.data;
  },
  getFollowing: async (
    searchTerm: string,
    orderBy: OrderUserSearch,
  ): Promise<IPagedResult<ICoreUser>> => {
    const response = await Api.get(`user/following`, {
      searchTerm: searchTerm,
      orderBy: orderBy,
    });
    return response.data;
  },
  getFollowingIds: async (
    searchTerm: string,
    orderBy: OrderUserSearch,
  ): Promise<string[]> => {
    const response = await Api.get(`user/following-ids`, {
      searchTerm: searchTerm,
      orderBy: orderBy,
    });
    return response.data;
  },
  createFollowing: async (userId: string, studentId: string): Promise<void> => {
    const response = await Api.post('user/following', {
      userId: userId,
      studentId: studentId,
    });
    if (response.status != 200) {
      throw response.status;
    }
  },
  removeFollowing: async (studentId: string): Promise<void> => {
    const response = await Api.delete(`user/following/${studentId}`);
    if (response.status != 200) {
      throw response.status;
    }
  },
};

export default UserService;
